import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  ArrowLeft,
  X
} from 'lucide-react';
import { exchangesAPI } from '../../api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAppStore } from '../../stores/useAppStore';
import { DataTable, Button } from '../ui';
import dayjs from '../../utils/dayjs';
import { readParam, useSyncUrlParams } from '../../hooks/useUrlListState';

const ExchangeList = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { addNotification } = useAppStore();

  // View state (search/sort/pagination) is seeded from the URL so the list is
  // shareable and restored when returning from an exchange's detail page.
  const [searchParams] = useSearchParams();
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // draftSearch = what's typed in the box; searchTerm = the applied query that
  // drives the fetch. They diverge until the user hits Search / Enter.
  const [draftSearch, setDraftSearch] = useState(() => readParam(searchParams, 'q', ''));
  const [searchTerm, setSearchTerm] = useState(() => readParam(searchParams, 'q', ''));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(() => readParam(searchParams, 'page', 1, 'number'));
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => readParam(searchParams, 'size', 20, 'number'));
  const [totalItems, setTotalItems] = useState(0);

  // Sorting state
  const [sortBy, setSortBy] = useState(() => readParam(searchParams, 'sort', 'ExchangeDate'));
  const [sortOrder, setSortOrder] = useState(() => readParam(searchParams, 'order', 'desc'));

  // Mirror the current view into the URL (defaults omitted).
  useSyncUrlParams(
    { q: searchTerm.trim(), sort: sortBy, order: sortOrder, page: currentPage, size: itemsPerPage },
    { q: '', sort: 'ExchangeDate', order: 'desc', page: 1, size: 20 },
  );

  // Reload whenever the applied query / page / sort / size changes. searchTerm
  // only changes when the user hits Search, so nothing loads as you type.
  useEffect(() => {
    loadExchanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, itemsPerPage, sortBy, sortOrder]);

  const loadExchanges = async () => {
    try {
      setLoading(true);
      setError('');
      
      const skip = (currentPage - 1) * itemsPerPage;
      const params = {
        limit: itemsPerPage,
        skip,
        sort: sortBy,
        order: sortOrder,
        ...(searchTerm && { search: searchTerm })
      };

      const response = await exchangesAPI.list(params);
      const data = response.data || [];
      setExchanges(data);

      // Naive next-page detection (matches the other list pages): offer a next
      // page while this one is full, stop when a short page comes back. The
      // endpoint returns a bare list with no total, so this is the page count.
      setTotalPages(data.length === itemsPerPage ? currentPage + 1 : currentPage);
      setTotalItems((currentPage - 1) * itemsPerPage + data.length);
    } catch (error) {
      console.error('Error loading exchanges:', error);
      setError('Failed to load exchanges');
      addNotification('Failed to load exchanges', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    // TableFooter's <select> passes the change event, not the value — read and
    // parse it (matching PurchaseList). Passing the event straight through made
    // itemsPerPage an object, producing limit=[object Object] & offset=NaN (422).
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Sorting handler
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Apply the typed search (Search button / Enter); reset to page 1. The reload
  // effect above does the fetch — nothing loads as you type.
  const handleSearch = () => {
    setSearchTerm(draftSearch);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setDraftSearch('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getExchangeStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DELETED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getExchangeDifferenceColor = (difference) => {
    if (difference > 0) {
      return 'text-red-600'; // Customer pays extra
    } else if (difference < 0) {
      return 'text-green-600'; // Store pays back
    } else {
      return 'text-gray-600'; // Even exchange
    }
  };

  const getExchangeDifferenceText = (difference) => {
    if (difference > 0) {
      return `Customer pays ${formatPrice(difference)}`;
    } else if (difference < 0) {
      return `Store pays ${formatPrice(Math.abs(difference))}`;
    } else {
      return 'Even exchange';
    }
  };

  const getWcSyncStatusBadge = (status) => {
    if (!status) return null;
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      synced: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        WC: {status}
      </span>
    );
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'ExchangeNo',
      title: 'Exchange #',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm font-medium text-gray-900">
          {row.ExchangeNo || `EX-${row.ID}`}
        </div>
      )
    },
    {
      key: 'ExchangeDate',
      title: 'Date',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          {dayjs(row.ExchangeDate).format('MMM DD, YYYY HH:mm')}
        </div>
      )
    },
    {
      key: 'CustomerName',
      title: 'Customer',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm text-gray-900">
            {row.CustomerName || 'Counter Exchange'}
          </div>
          {row.CustomerPhone && (
            <div className="text-sm text-gray-500">
              {row.CustomerPhone}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'TotalGivenValue',
      title: 'Given Value',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          {formatPrice(row.TotalGivenValue)}
        </div>
      )
    },
    {
      key: 'TotalReturnValue',
      title: 'Return Value',
      sortable: true,
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          {formatPrice(row.TotalReturnValue)}
        </div>
      )
    },
    {
      key: 'ExchangeDifference',
      title: 'Difference',
      sortable: true,
      render: (value, row) => (
        <div className={`text-sm font-medium ${getExchangeDifferenceColor(row.ExchangeDifference)}`}>
          {getExchangeDifferenceText(row.ExchangeDifference)}
        </div>
      )
    },
    {
      key: 'Status',
      title: 'Status',
      sortable: true,
      render: (value, row) => (
        <div className="flex flex-col gap-1">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getExchangeStatusColor(row.Status)}`}>
            {row.Status || 'ACTIVE'}
          </span>
          {getWcSyncStatusBadge(row.wc_sync_status)}
        </div>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(`/exchange/${row.ID}`)}
            className="text-blue-600 hover:text-blue-900 transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Exchanges</h1>
        </div>
        
        <Button
          onClick={() => navigate('/exchange/new')}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>New Exchange</span>
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search exchanges by number, customer, or phone..."
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              style={{ fontSize: '1rem' }}
              className="w-full h-12 pl-11 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {draftSearch && (
              <button
                onClick={clearSearch}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="h-12 px-6 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium whitespace-nowrap"
          >
            <Search className="h-5 w-5" />
            Search
          </button>
        </div>
      </div>

      {/* Search Results Indicator */}
      {searchTerm.trim() && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-blue-800">Showing results for "{searchTerm}"</p>
            <button onClick={clearSearch} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Clear Search</button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={exchanges}
        loading={loading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        itemLabel="exchanges"
        onRowClick={(row, e) =>
          e.metaKey || e.ctrlKey
            ? window.open(`/exchange/${row.ID}`, '_blank', 'noopener')
            : navigate(`/exchange/${row.ID}`)
        }
        itemsPerPageOptions={[10, 20, 50, 100]}
        showFooter={true}
        className="space-y-4"
        tableClassName="min-w-full"
      />
    </div>
  );
};

export default ExchangeList;
